import { Injectable } from '@angular/core';
import { ConfigurationService } from '@config/configuration.service';
import { HttpClient } from '@angular/common/http';
import {
    PortfolioAPIResponse,
    PortfoliosResponse,
    portfolioMarketPlaceRequest,
    MarketPlacePortfolioRequest,
    RecommendedOriginalsResponse
} from './market-place.interface';
import { catchError, map, share, finalize } from 'rxjs/operators';
import { BriefPortfolioModel, BriefStrategyModel } from './market-place.models';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { CustomEvents, STRATEGY_CREATION_METHOD, Utils } from '@app/_helpers';
import { MyStrategyType } from '../../../my-strategies/my-strategies.consts';
import { BooksSocketService } from '@app/_shared/providers/websocket/books-socket/books-socket.service';
import { SubjectType, StrategySocketUpdateI, ConfirmationDialogService, GLOBAL_EXIT_BREACH, EventsService, ConfirmationDialogComponent } from '@app/_shared';
import { WebApiStrategyResponseType } from '../../../../../../../utProto/webApiStrategyEnums_pb';
import { GenericMessageResponse, GlobalExitStatusResponse } from '@app/app.interface';
import { MarketPlaceCardsType } from '../../../dashboard/components/marketplace-cards/marketplace-cards.consts';
import { Router } from '@angular/router';
import { MbPlansService } from '../../../mb-plans/providers/mb-plans.service';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

@Injectable({
    providedIn: 'root'
})
export class MarketPlaceService {
    private marketPlaceListingCache = new Map<string, BriefStrategyModel[]>();
    private deployedStrategiesCache = new Map<string, BriefStrategyModel>();
    private totalPages: number;
    public totalCreatedPortfolios = 0;
    public savedPortfolioCache =  new Set<string>();
    public cachedMarketplacePortfolioResponse: PortfoliosResponse;
    public deployedStrategies: BriefPortfolioModel[] = [];

    private subscriptions = new Subscription();
    isDeployedPortfoliosFetched = new BehaviorSubject<boolean>(false);
    portfoliosCachedByType = new Map<string, BriefPortfolioModel[]>();
    marginRecommendationMap = new Map<string, RecommendedOriginalsResponse>();
    private activeRequests = new Map<string, Observable<PortfoliosResponse>>();

    portfolioTabsIndices: string[] = Object.keys(MyStrategyType);

    constructor(
        private conf: ConfigurationService,
        private http: HttpClient,
        private socketService: BooksSocketService,
        private confirmationService: ConfirmationDialogService,
        private router: Router,
        private eventsService: EventsService,
        private mbPlansService: MbPlansService
    ) {
        this.subscriptions.add(
            this.socketService.getSubject(SubjectType.STRATEGY).subscribe((socketUpdate: StrategySocketUpdateI) => {
                if ((socketUpdate.webApiStrategyResponseType === WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_TERMINATE_SUCCESS) ||
                    (socketUpdate.webApiStrategyResponseType === WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_STRATEGY_WAIT)) {
                    for (let [key, value] of Array.from(this.deployedStrategiesCache)) {
                        if (value.UniqueAlgoName === socketUpdate.uniqueAlgoName) {
                            this.deployedStrategiesCache.delete(key);
                        }
                    }
                }
            })
        );
    }

    updateTabIndices() {
        const personalDetails = this.mbPlansService.userDetails;
        const showAiRecommended = personalDetails.isOneAiAllowed() && personalDetails.isAiRecommendOriginalsAllowed() && this.conf.remoteConfigCacheObj?.SHOW_AI_RECOMMENDED;
        if(!showAiRecommended) {
            this.portfolioTabsIndices = this.portfolioTabsIndices.filter(tab => tab !== MyStrategyType.AiRecommended);
        }
        this.eventsService.publish(CustomEvents.TAB_INDICES_UPDATED);
    }

    // Returns unique algo name for deployed strategy from cached deployed strategies map.
    // getUniqueAlgoName(strategyID: string)  {
    //   if (this.deployedStrategiesCache.size && this.deployedStrategiesCache.has(strategyID)) {
    //     return of(this.deployedStrategiesCache.get(strategyID));
    //   } else {
    //     return this.getAllDeployedStrategies(true).pipe(map((res) => {
    //       return res.get(strategyID);
    //     }));
    //   }
    // }

    // To remove  strategy from deployed strategy cache upon undeploy.
    removeDeployedStrategyFromCache(strategyID: string) {
        this.deployedStrategiesCache.delete(strategyID);
    }

    getPortfolioMarketPlaceListing(request: portfolioMarketPlaceRequest) {
        return this.http.get<PortfolioAPIResponse>(this.conf.applicationUrls.portfolioMarketPlace, {
            params: Utils.removeUndefined(request) as any
        })
            .pipe(
                map((res) => {
                    const convertedResponse: PortfoliosResponse = {
                        page_number: res.page_number,
                        page_size: res.page_size,
                        total_pages: res.total_pages,
                        data: res.data.portfolios.map(r =>{
                            const statuses = res.data.portfolio_statuses[r._id];
                            const backtestStatuses = res.data.backtest_statuses[r._id];
                            const dynamicCardFields = res.data.extra_dynamic_card_fields?.[r._id] || null;
                            const status = statuses?.length > 0 ? statuses[statuses.length - 1] : null;
                            const winsLosses = res.data?.wins_losses_map?.[r._id] || null;
                            const portfolio =  new BriefPortfolioModel(r, status,backtestStatuses, winsLosses, dynamicCardFields);
                            if(res.data.original_portfolio_details && res.data.original_portfolio_details[portfolio.originalPortfolioId]){
                                portfolio.updateOriginalPortfolioDetails([res.data.original_portfolio_details[portfolio.originalPortfolioId]]);
                            }
                            return portfolio;
                        }),
                        total_count: res.total_count
                    }

                    this.totalPages = res.total_pages;
                    return convertedResponse;
                })
            );
    }

    getMarketPlacePortfolioByType(type: MarketPlaceCardsType, request: MarketPlacePortfolioRequest) {
        var url = this.conf.applicationUrls.portfolioMarketPlace;

        switch (type) {
            case MarketPlaceCardsType.Subscribed:
                url = this.conf.applicationUrls.getSubscribedPortfolio;
                break;

            case MarketPlaceCardsType.Verified:
            case MarketPlaceCardsType.Originals:
                url = this.conf.applicationUrls.portfolioMarketPlace;
                request.strategy_type = type;
                break;

            case MarketPlaceCardsType.Created:
                url = this.conf.applicationUrls.getCreatedPortfolio;
                break;

            case MarketPlaceCardsType.Bookmarked:
                url = this.conf.applicationUrls.getBookmarkedPortfolio;
                break;

            default:
                break;
        }

        return this.http.get<PortfolioAPIResponse>(url, {
            params: Utils.removeUndefined(request) as any
        })
            .pipe(
                map((res) => {
                    const convertedResponse: PortfoliosResponse = {
                        page_number: res.page_number,
                        page_size: res.page_size,
                        total_pages: res.total_pages,
                        data:  res.data.portfolios.map(r =>{
                            const statuses = res.data.portfolio_statuses?.[r._id];   
                            const backtestStatuses = res.data.backtest_statuses[r._id];
                            const dynamicCardFields = res.data.extra_dynamic_card_fields?.[r._id] || null;
                            const status = statuses?.length > 0 ? statuses[statuses.length - 1] : null;
                            const winsLosses = res.data?.wins_losses_map?.[r._id] || null;
                            const portfolio = new BriefPortfolioModel(r, status,backtestStatuses, winsLosses, dynamicCardFields);
                            if(res.data.original_portfolio_details && res.data.original_portfolio_details[portfolio.originalPortfolioId]){
                                portfolio.updateOriginalPortfolioDetails([res.data.original_portfolio_details[portfolio.originalPortfolioId]])
                            }
                            return portfolio;
                        }),
                        total_count: res.total_count
                    } 
                    this.totalPages = res.total_pages;
                    this.portfoliosCachedByType.set(type, convertedResponse.data);
                    return this.cachedMarketplacePortfolioResponse = convertedResponse;
                }),
                catchError(err => {
                    return of(this.cachedMarketplacePortfolioResponse);
                })
            );
    }

    getPortfoliosByType(type: MyStrategyType, request: MarketPlacePortfolioRequest) {
        let url = '';
        switch (type) {
            case MyStrategyType.Created:
                url = this.conf.applicationUrls.getCreatedPortfolio;
                break;

            case MyStrategyType.Deployed:
                url = this.conf.applicationUrls.getDeployedPortfolio;
                break;

            case MyStrategyType.Subscribed:
                url = this.conf.applicationUrls.getSubscribedPortfolio;
                break;

            case MyStrategyType.Saved:
                url = this.conf.applicationUrls.getBookmarkedPortfolio;
                break;

            case MyStrategyType.Shared:
                url = this.conf.applicationUrls.getSharedPortfolio;
                break;

            case MyStrategyType.Originals:
                url = this.conf.applicationUrls.portfolioMarketPlace;
                request.strategy_type = MarketPlaceCardsType.Originals;
                break;
        }

        // Create a unique key for the request including query params
        const params = Utils.removeUndefined(request);
        const requestKey = url + JSON.stringify(params);

        // Check if there's an active request for this URL + params combination
        if (this.activeRequests.has(requestKey)) {
            return this.activeRequests.get(requestKey);
        }

        // Create new request observable
        const request$ = this.http.get<PortfolioAPIResponse>(url, { params: params as any })
            .pipe(
                map((res) => {
                    const convertedResponse: PortfoliosResponse = {
                        page_number: res.page_number,
                        page_size: res.page_size,
                        total_pages: res.total_pages,
                        data: url === this.conf.applicationUrls.getDeployedPortfolio ? 
                        res.data.deployed_portfolios.map(r => {
                            const status = r.portfolioStatus;
                            const dynamicCardFields = r.extraDynamicCardFields || null;
                            const portfolio =  new BriefPortfolioModel(r.portfolioDetails, status,null, null, dynamicCardFields);
                            if(res.data.original_portfolio_details && res.data.original_portfolio_details[portfolio.originalPortfolioId]){
                                portfolio.updateOriginalPortfolioDetails([res.data.original_portfolio_details[portfolio.originalPortfolioId]])
                            }
                            return portfolio;
                        }) : res.data.portfolios.map(r =>{
                            const statuses = res.data.portfolio_statuses[r._id];
                            const backtestStatuses = res.data.backtest_statuses[r._id];
                            const dynamicCardFields = res.data.extra_dynamic_card_fields?.[r._id] || null;
                            const status = statuses?.length > 0 ? statuses[statuses.length - 1] : null;
                            const winsLosses = res.data?.wins_losses_map?.[r._id] || null;
                            const portfolio = new BriefPortfolioModel(r, status,backtestStatuses, winsLosses, dynamicCardFields);
                            if(res.data.original_portfolio_details && res.data.original_portfolio_details[portfolio.originalPortfolioId]){
                                portfolio.updateOriginalPortfolioDetails([res.data.original_portfolio_details[portfolio.originalPortfolioId]])
                            }
                            return portfolio;
                        }),
                        total_count: res.total_count
                    }
                    this.totalPages = res.total_pages;

                    // If Strategy type is created then count total number of portfolios
                    if (type == MyStrategyType.Created) {
                        this.totalCreatedPortfolios = convertedResponse.data.
                            filter(portfolio => portfolio.creationMethod == STRATEGY_CREATION_METHOD.PORTFOLIO).length;
                    }

                    if( type == MyStrategyType.Deployed) {
                        this.deployedStrategies = convertedResponse.data;
                        //whenever the deployed response is fetched, update the tradebook algo name
                        this.isDeployedPortfoliosFetched.next(true);
                        this.eventsService.publish(CustomEvents.PortfolioStatusEvent);
                    }

                    this.portfoliosCachedByType.set(type, convertedResponse.data);
                    return convertedResponse;
                }),
                share(),
                finalize(() => {
                    // Remove the request from active requests when completed
                    this.activeRequests.delete(requestKey);
                })
            );

        // Store the request
        this.activeRequests.set(requestKey, request$);

        return request$;
    }

    getPortfolioCached(uniquePortfolioID: string, portfolioID:string, type: MyStrategyType | MarketPlaceCardsType) {
        if (type === MyStrategyType.Deployed) {
            return this.portfoliosCachedByType.get(type)?.find(portfolio => portfolio.uniquePortfolioID === uniquePortfolioID);
        } else {
            return this.portfoliosCachedByType.get(type)?.find(portfolio => portfolio.portfolioID === portfolioID);
        }
        
    }

    /**
     * Inserts or replaces a portfolio in the list cache (matched by portfolioID or uniquePortfolioID).
     */
    upsertPortfolioInTypeCache(portfolio: BriefPortfolioModel, type: MyStrategyType | MarketPlaceCardsType): void {
        if (!portfolio || !type) {
            return;
        }
        const cache = this.portfoliosCachedByType;
        let list = cache.get(type);
        if (!list) {
            list = [];
            cache.set(type, list);
        }
        /*
         * Match on uniquePortfolioID for Deployed (same comparator used by
         * StrategiesService.updatePortfoliosData) and portfolioID for the
         * other types, so we don't accidentally store duplicates.
         */
        const isDeployed = type === MyStrategyType.Deployed;
        const existingIndex = list.findIndex(p => isDeployed
            ? p.uniquePortfolioID === portfolio.uniquePortfolioID
            : p.portfolioID === portfolio.portfolioID);
        if (existingIndex === -1) {
            list.push(portfolio);
        } else {
            /*
             * Replace the cached instance with the freshly fetched one so
             * subsequent socket mutations write into the same model that
             * this component is rendering. Preserve dynamic card fields when
             * the new fetch omitted `extra_dynamic_card_fields`.
             */
            portfolio.inheritDynamicCardFieldsFrom(list[existingIndex]);
            list[existingIndex] = portfolio;
        }
    }

    openGlobalExitBreachPopup(portfolioName: string) {
        return this.confirmationService.alert({
            message: GLOBAL_EXIT_BREACH + Utils.boldText(portfolioName),
            buttons: [
                {
                    text: 'Change Settings',
                    accent: true,
                    class: 'new-theme-gradient-bg',
                    focused: true,
                    handler: () => {
                       this.router.navigate(['/trading/strategies/global-exit-settings']);
                    }
                }]
        })
    }

    checkGlobalExitBreach(): Observable<GlobalExitStatusResponse> {
        return this.http.get<GlobalExitStatusResponse>(this.conf.applicationUrls.globalExitStatus);
    }

    /**
     * Checks if deployment should proceed by verifying global exit breach status.
     * Forward testing portfolios bypass this check as they don't require global exit verification.
     * 
     * @param portfolioName Name of the portfolio being deployed (used in error dialog)
     * @param isForwardSelected Whether this is a forward testing portfolio
     * @returns Observable containing deployment permission status and optional dialog reference for user interaction
     */
    checkGlobalExitBeforeDeployment(portfolioName: string, isForwardSelected: boolean): Observable<{ canDeploy: boolean; dialogRef?: MatDialogRef<ConfirmationDialogComponent> }> {
        // If the portfolio is a forward selected portfolio, then we can deploy it 
        // as there is no need to check the global exit breach for forward testing
        if (isForwardSelected) {
            return of({ canDeploy: true });
        }

        return this.checkGlobalExitBreach().pipe(
            map(data => {
                if (data?.isExited) {
                    const dialogRef = this.openGlobalExitBreachPopup(portfolioName);
                    return { canDeploy: false, dialogRef };
                }
                return { canDeploy: true };
            }),
            catchError(err => {
                console.error('checkGlobalExitBreach error', err);
                return of({ canDeploy: false });
            })
        );
    }

    setBookmark(strategyID: string) {
        return this.http.put<GenericMessageResponse>(this.conf.applicationUrls.portfolioBookmark + '/' + strategyID, null);
    }

    subscribePortfolio(strategyId: string) {
        return this.http.put<GenericMessageResponse>(this.conf.applicationUrls.portfolioSubscribe + '/' + strategyId, null);
    }

    unsetBookmark(strategyID: string) {
        return this.http.put<GenericMessageResponse>(this.conf.applicationUrls.portfolioBookmark + '/' + strategyID, null);
    }

    unsubscribePortfolio(strategyId: string) {
        return this.http.put<GenericMessageResponse>(this.conf.applicationUrls.portfolioSubscribe + '/' + strategyId, null);
    }

    getRecommendedOriginals(totalCapital: number, plan: string): Observable<RecommendedOriginalsResponse> {
        const cachedRecommendations = this.marginRecommendationMap.get(totalCapital.toString());
        if (cachedRecommendations) {
            return of(cachedRecommendations);
        }

        return this.http.post<RecommendedOriginalsResponse>(this.conf.applicationUrls.aiRecommendedOriginals, {
            totalCapital: totalCapital.toString(),
            day: 'today',
            user_plan_name: plan.toLowerCase(),
            upsell_allocation: true
        }).pipe(
            map(res => {
                this.marginRecommendationMap.set(totalCapital.toString(), res);
                return res;
            })
        );
    }
}
